import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useMemo, useRef } from 'react';
import SoftPullStatus from '~/constants/softPullStatus';
import { useUserLocation } from '~/context/common/UserContext';
import { useSoonestRoute } from '~/context/vdp/DeliveryPickupContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import usePaymentSummary from '~/hooks/vdp/selectors/useGetPaymentSummary';
import useShouldDisplayOrderStatus from '~/hooks/vdp/selectors/useShouldDisplayOrderStatus';
import useSoftPullStatus from '~/hooks/vdp/selectors/useSoftPullStatus';
import { PricingError } from '~/services/common/contracts/PricingResponse';

export default function useSendSptEventAndActions() {
  const { vehicleId = 0 } = useGetVehicleDetails();
  const {
    vehicleAvailability: { isLoaded: isVehicleAvailabilityLoaded },
    vehiclePricingInfo: { data, isLoaded },
    vehiclePricingError
  } = useContext(UserVehicleContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { isSold, otherUnavailable, unavailable } = useAlertStatus();
  const { softPullStatus } = useSoftPullStatus();
  const hasSoftPull = softPullStatus === SoftPullStatus.Active;
  const sendData =
    vehiclePricingError === PricingError.None ||
    vehiclePricingError === PricingError.NoVehiclePricingInfo;
  const shouldDisplayOrderStatus = useShouldDisplayOrderStatus();
  const { zip } = useUserLocation();
  const { activityType } = useSoonestRoute() ?? {};
  const {
    totals: { totalPrice }
  } = usePaymentSummary();

  const initialEventRef = useRef(true);
  const zipRef = useRef(zip);

  useEffect(() => {
    zipRef.current = zip;
  }, [zip]);

  const eventData = useMemo(
    () =>
      sendData
        ? {
            error: vehiclePricingError,
            financingType: data.financeInfo?.financingType,
            hasSoftPull,
            isInitialEvent: initialEventRef.current,
            isTradeInApplied: data.tradeInfo?.applyTradeIn ?? null,
            merchSelectedActivityType: activityType,
            priceInfo: data.price,
            transportCost: data.price?.transportCost,
            totalPurchasePrice: totalPrice,
            pricingSearchRequestId: data.searchRequestId,
            zip: zipRef.current
          }
        : {
            error: vehiclePricingError,
            isInitialEvent: initialEventRef.current
          },
    [
      activityType,
      data.financeInfo?.financingType,
      data.price,
      data.searchRequestId,
      data.tradeInfo?.applyTradeIn,
      hasSoftPull,
      sendData,
      totalPrice,
      vehiclePricingError
    ]
  );

  useEffect(() => {
    if (!isLoaded) return;
    trackEvent('SPT Data', eventData, undefined, {
      useFetch: true,
      fetchOption: { keepalive: false }
    });
    initialEventRef.current = false;
  }, [eventData, isLoaded, trackEvent]);

  useEffect(() => {
    if (!shouldDisplayOrderStatus) return;

    datadogRum.addAction('SPT displayed order status card');
  }, [shouldDisplayOrderStatus]);

  // Sold & unavailable vehicles may no longer have pricing info, so do not send it.
  const sendHealthAction =
    isLoaded &&
    isVehicleAvailabilityLoaded &&
    !isSold &&
    !otherUnavailable &&
    !unavailable;

  useEffect(() => {
    if (!sendHealthAction) return;

    datadogRum.addAction('SPT Health', {
      status: vehiclePricingError,
      vehicleId
    });
  }, [sendHealthAction, vehicleId, vehiclePricingError]);
}
